import { useState, useEffect, useRef, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { renderSystemMessage } from './system-message';
import {
  Send,
  Loader2,
  MessageCircle,
  Check,
  CheckCheck,
  Smile,
  Pencil,
  X,
  Paperclip,
  FileIcon,
} from 'lucide-react';
import { useAdminChatWs, type WsNewMessage } from '@/hooks/use-admin-chat-ws';
import { resolveApiUrl } from '@/providers/api-url';
import { MessageDeliveryStatus } from '@masters-club/types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MessageSender {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl?: string | null;
  email?: string;
}

interface MessageReaction {
  emoji: string;
  count: number;
  hasReacted: boolean;
}

interface MessageReadBy {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  username: string | null;
  readAt: string;
}

interface MessageAttachment {
  id: number;
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

interface ChatMessage {
  id: number;
  chatRoomId: number;
  content: string;
  isSystemMessage: boolean;
  replyToMessageId: number | null;
  status: MessageDeliveryStatus;
  isEdited: boolean;
  editedAt: string | null;
  isDeleted: boolean;
  createdAt: string;
  sender: MessageSender | null;
  replyTo: ChatMessage | null;
  reactions: MessageReaction[];
  readBy: MessageReadBy[];
  attachments?: MessageAttachment[];
}

interface TypingUser {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  username: string | null;
}

interface ChatPanelProps {
  chatRoomId: number | null;
  userId?: number;
  className?: string;
  height?: string;
  showHeader?: boolean;
  headerTitle?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface ChatRoom {
  id: number;
  type: string;
  name: string | null;
  isActive: boolean;
}

interface UploadedFile {
  id: number;
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const API_URL = resolveApiUrl();

const apiFetch = async <T,>(url: string, init?: RequestInit): Promise<T | null> => {
  const response = await fetch(`${API_URL}${url}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  if (!response.ok) { return null; }

  const json = (await response.json()) as ApiResponse<T>;
  return json.data ?? null;
};

const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const getSenderName = (sender: ChatMessage['sender']): string => {
  if (!sender) { return 'Система'; }
  if (sender.type === 'admin') {
    return sender.username || sender.email || 'Админ';
  }
  const parts = [sender.firstName, sender.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : sender.username || 'Пользователь';
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) { return `${bytes} Б`; }
  if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} КБ`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const isImageMimeType = (mimeType: string): boolean =>
  mimeType.startsWith('image/');

const sortByCreatedAt = (msgs: ChatMessage[]): ChatMessage[] =>
  [...msgs].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

const groupMessagesByDate = (messages: ChatMessage[]): Array<{ dateKey: string; dateLabel: string; messages: ChatMessage[] }> => {
  const groups: Array<{ dateKey: string; dateLabel: string; messages: ChatMessage[] }> = [];
  let currentGroup: { dateKey: string; dateLabel: string; messages: ChatMessage[] } | null = null;

  for (const msg of messages) {
    const dateKey = new Date(msg.createdAt).toDateString();
    if (!currentGroup || currentGroup.dateKey !== dateKey) {
      currentGroup = { dateKey, dateLabel: formatDate(msg.createdAt), messages: [] };
      groups.push(currentGroup);
    }
    currentGroup.messages.push(msg);
  }

  return groups;
};

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥', '👏', '🙏'];

// ─── Component ────────────────────────────────────────────────────────────────

export function ChatPanel({
  chatRoomId: externalRoomId,
  userId,
  className,
  height = '500px',
  showHeader = true,
  headerTitle = 'Чат с клиентом',
}: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [resolvedRoomId, setResolvedRoomId] = useState<number | null>(externalRoomId);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Помечаем, что админ уже auto-join в комнату (через первый REST-запрос)
  // — backend handleSubscribeRoom проверяет isParticipant и блокирует WS-
  // подписку, поэтому subscribe откладываем до завершения fetchMessages.
  const [joinedRoom, setJoinedRoom] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Ref для markRead — чтобы обойти circular dependency с useAdminChatWs
  const markReadRef = useRef<(roomId: number, messageId: number) => void>(() => {});

  const roomId = resolvedRoomId;

  // ─── WebSocket ──────────────────────────────────────────────────────

  const wsCallbacks = {
    enabled: roomId !== null,

    onNewMessage: useCallback((wsMsg: WsNewMessage) => {
      if (wsMsg.chatRoomId !== roomId) { return; }

      const mapped: ChatMessage = {
        ...wsMsg,
        sender: wsMsg.sender,
        replyTo: null,
      };

      setMessages((prev) => {
        if (prev.some((m) => m.id === mapped.id)) { return prev; }
        return sortByCreatedAt([...prev, mapped]);
      });

      // Отмечаем как прочитанное через ref (избегаем circular dep)
      if (roomId !== null) {
        markReadRef.current(roomId, wsMsg.id);
      }
    }, [roomId]),

    onMessageEdited: useCallback((data: { messageId: number; roomId: number; content: string; editedAt: string }) => {
      if (data.roomId !== roomId) { return; }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === data.messageId
            ? { ...m, content: data.content, isEdited: true, editedAt: data.editedAt }
            : m,
        ),
      );
    }, [roomId]),

    onMessageDeleted: useCallback((data: { messageId: number; roomId: number }) => {
      if (data.roomId !== roomId) { return; }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === data.messageId
            ? { ...m, isDeleted: true, content: '' }
            : m,
        ),
      );
    }, [roomId]),

    onTyping: useCallback((data: { roomId: number; user: TypingUser }) => {
      if (data.roomId !== roomId) { return; }
      if (data.user.type === 'admin') { return; }

      const key = `${data.user.type}-${data.user.id}`;

      setTypingUsers((prev) => {
        if (prev.some((u) => u.type === data.user.type && u.id === data.user.id)) { return prev; }
        return [...prev, data.user];
      });

      const existingTimeout = typingTimeoutsRef.current.get(key);
      if (existingTimeout) { clearTimeout(existingTimeout); }
      typingTimeoutsRef.current.set(key, setTimeout(() => {
        setTypingUsers((prev) => prev.filter((u) => !(u.type === data.user.type && u.id === data.user.id)));
        typingTimeoutsRef.current.delete(key);
      }, 5000));
    }, [roomId]),

    onStoppedTyping: useCallback((data: { roomId: number; userId: number; userType: 'user' | 'admin' }) => {
      if (data.roomId !== roomId) { return; }
      const key = `${data.userType}-${data.userId}`;
      const existingTimeout = typingTimeoutsRef.current.get(key);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        typingTimeoutsRef.current.delete(key);
      }
      setTypingUsers((prev) => prev.filter((u) => !(u.type === data.userType && u.id === data.userId)));
    }, [roomId]),

    onMessageRead: useCallback((data: { messageId: number; roomId: number; reader: TypingUser; readAt: string }) => {
      if (data.roomId !== roomId) { return; }
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== data.messageId) { return m; }
          const alreadyRead = m.readBy.some((r) => r.type === data.reader.type && r.id === data.reader.id);
          if (alreadyRead) { return m; }
          return {
            ...m,
            status: MessageDeliveryStatus.READ,
            readBy: [...m.readBy, {
              type: data.reader.type,
              id: data.reader.id,
              firstName: data.reader.firstName,
              username: data.reader.username,
              readAt: data.readAt,
            }],
          };
        }),
      );
    }, [roomId]),
  };

  const { isConnected, subscribe, unsubscribe, markRead } = useAdminChatWs(wsCallbacks);

  // Синхронизируем ref с markRead
  useEffect(() => {
    markReadRef.current = markRead;
  }, [markRead]);

  // ─── Resolve room ───────────────────────────────────────────────────

  useEffect(() => {
    setResolvedRoomId(externalRoomId);
  }, [externalRoomId]);

  useEffect(() => {
    if (externalRoomId !== null || userId === undefined) { return; }

    let cancelled = false;

    const findOrCreateChat = async () => {
      const room = await apiFetch<ChatRoom>(`/admin/messaging/user/${userId}/chat`, {
        method: 'POST',
      });
      if (!cancelled && room?.id) {
        setResolvedRoomId(room.id);
      }
    };

    void findOrCreateChat();
    return () => { cancelled = true; };
  }, [externalRoomId, userId]);

  // ─── Fetch messages ─────────────────────────────────────────────────

  const fetchMessages = useCallback(async () => {
    if (roomId === null) { return; }

    const data = await apiFetch<ChatMessage[]>(
      `/admin/messaging/rooms/${roomId}/messages?limit=50`,
    );

    if (data) {
      setMessages(sortByCreatedAt(data));
    }
    setIsLoading(false);
    // REST-роут /admin/messaging/rooms/:roomId/messages выполняет auto-join
    // админа в ChatParticipant. После этого можно безопасно subscribe через WS.
    setJoinedRoom(roomId);
  }, [roomId]);

  useEffect(() => {
    if (roomId === null) {
      setIsLoading(false);
      setJoinedRoom(null);
      return;
    }
    setIsLoading(true);
    setJoinedRoom(null);
    void fetchMessages();
  }, [roomId, fetchMessages]);

  // ─── WS subscribe ───────────────────────────────────────────────────
  // Подписываемся ТОЛЬКО после успешного auto-join через REST — иначе
  // backend handleSubscribeRoom вернёт NOT_PARTICIPANT и realtime не работает.

  useEffect(() => {
    if (roomId === null || !isConnected) { return; }
    if (joinedRoom !== roomId) { return; }
    subscribe(roomId);
    return () => { unsubscribe(roomId); };
  }, [roomId, joinedRoom, isConnected, subscribe, unsubscribe]);

  // ─── Auto-scroll ────────────────────────────────────────────────────
  // Radix ScrollArea: реальный скролл живёт внутри Viewport, ref у Root
  // указывает на корневой div и не имеет нужной высоты. Достаём Viewport
  // через data-slot, чтобы scrollTop = scrollHeight реально сработал.

  useEffect(() => {
    if (!scrollViewportRef.current && scrollRef.current) {
      scrollViewportRef.current = scrollRef.current.querySelector<HTMLDivElement>(
        '[data-slot="scroll-area-viewport"]',
      );
    }
    const viewport = scrollViewportRef.current;
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [messages]);

  // ─── Cleanup ────────────────────────────────────────────────────────

  useEffect(() => {
    const timeouts = typingTimeoutsRef.current;
    return () => {
      for (const timeout of timeouts.values()) {
        clearTimeout(timeout);
      }
      timeouts.clear();
    };
  }, []);

  // ─── Send / Edit message ───────────────────────────────────────────

  const handleSend = async () => {
    if (!inputText.trim() || isSending || roomId === null) { return; }

    setIsSending(true);
    const content = inputText.trim();
    setInputText('');

    if (editingMessage) {
      // Редактирование
      const result = await apiFetch<{ message: string }>(
        `/admin/chat/messages/${editingMessage.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ content }),
        },
      );

      if (result) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === editingMessage.id
              ? { ...m, content, isEdited: true, editedAt: new Date().toISOString() }
              : m,
          ),
        );
      } else {
        setInputText(content);
      }
      setEditingMessage(null);
    } else {
      // Новое сообщение
      const result = await apiFetch<ChatMessage>(
        `/admin/messaging/rooms/${roomId}/messages`,
        {
          method: 'POST',
          body: JSON.stringify({ content }),
        },
      );

      if (!result) {
        setInputText(content);
      } else {
        // Локально показываем сразу — не ждём WS-broadcast (он может
        // не дойти если WS не подписан). Дедуп по id предотвратит дубль
        // когда WS всё-таки доставит то же сообщение.
        setMessages((prev) => {
          if (prev.some((m) => m.id === result.id)) { return prev; }
          return sortByCreatedAt([...prev, result]);
        });
      }
    }

    setIsSending(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
    if (e.key === 'Escape' && editingMessage) {
      setEditingMessage(null);
      setInputText('');
    }
  };

  const startEditing = (msg: ChatMessage) => {
    setEditingMessage(msg);
    setInputText(msg.content);
    textareaRef.current?.focus();
  };

  const cancelEditing = () => {
    setEditingMessage(null);
    setInputText('');
  };

  // ─── Reactions ──────────────────────────────────────────────────────

  const toggleReaction = async (messageId: number, emoji: string, hasReacted: boolean) => {
    if (hasReacted) {
      await apiFetch<{ message: string }>(
        `/admin/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
        { method: 'DELETE' },
      );
    } else {
      await apiFetch<{ message: string }>(
        `/admin/chat/messages/${messageId}/reactions`,
        {
          method: 'POST',
          body: JSON.stringify({ emoji }),
        },
      );
    }
    // Перезагружаем сообщения для актуальных данных
    void fetchMessages();
  };

  // ─── File upload ────────────────────────────────────────────────────

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || roomId === null) { return; }

    setIsUploading(true);

    // Upload file
    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadResponse = await fetch(`${API_URL}/admin/messaging/rooms/${roomId}/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!uploadResponse.ok) {
        setIsUploading(false);
        return;
      }

      const uploadJson = (await uploadResponse.json()) as ApiResponse<UploadedFile>;
      const uploadedFile = uploadJson.data;

      if (!uploadedFile) {
        setIsUploading(false);
        return;
      }

      // Отправить сообщение с вложением
      const result = await apiFetch<ChatMessage>(
        `/admin/messaging/rooms/${roomId}/messages`,
        {
          method: 'POST',
          body: JSON.stringify({
            content: file.name,
            attachmentFileIds: [uploadedFile.id],
          }),
        },
      );

      if (result) {
        // Локально пушим (дедуп по id перекроет дубль с WS)
        setMessages((prev) => {
          if (prev.some((m) => m.id === result.id)) { return prev; }
          return sortByCreatedAt([...prev, result]);
        });
      }
    } catch {
      // ignore
    } finally {
      setIsUploading(false);
      // Сбросить input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // ─── Status icon ────────────────────────────────────────────────────

  const getStatusIcon = (msg: ChatMessage) => {
    if (msg.sender?.type !== 'admin') { return null; }

    const userReads = msg.readBy.filter((r) => r.type === 'user');

    if (userReads.length > 0) {
      return (
        <span className="inline-flex items-center gap-0.5 text-blue-500" title={`Прочитано в ${formatTime(userReads[0].readAt)}`}>
          <CheckCheck className="h-3.5 w-3.5" />
        </span>
      );
    }

    if (msg.status === MessageDeliveryStatus.READ) {
      return (
        <span className="inline-flex text-blue-500" title="Прочитано">
          <CheckCheck className="h-3.5 w-3.5" />
        </span>
      );
    }

    if (msg.status === MessageDeliveryStatus.DELIVERED) {
      return (
        <span className="inline-flex text-muted-foreground" title="Доставлено">
          <CheckCheck className="h-3.5 w-3.5" />
        </span>
      );
    }

    return (
      <span className="inline-flex text-muted-foreground" title="Отправлено">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  };

  // ─── Computed ───────────────────────────────────────────────────────

  const typingText = typingUsers.length > 0
    ? typingUsers.map((u) => u.firstName || u.username || 'Пользователь').join(', ') + ' печатает...'
    : null;

  const groups = groupMessagesByDate(messages);

  // ─── Loading ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div
        className={cn('flex items-center justify-center rounded-lg border', className)}
        style={{ height }}
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────

  return (
    <div
      className={cn('flex flex-col rounded-lg border bg-background', className)}
      style={{ height }}
    >
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{headerTitle}</span>
            {isConnected && (
              <span className="h-2 w-2 rounded-full bg-green-500" title="WebSocket подключён" />
            )}
          </div>
          {messages.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {messages.length} сообщений
            </Badge>
          )}
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0 px-4" ref={scrollRef}>
        <div className="space-y-1 py-4">
          {messages.length === 0 ? (
            <div className="flex h-full items-center justify-center py-20">
              <div className="text-center text-sm text-muted-foreground">
                <MessageCircle className="mx-auto mb-2 h-8 w-8 opacity-50" />
                <p>Нет сообщений</p>
                <p className="text-xs">Напишите первое сообщение клиенту</p>
              </div>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.dateKey}>
                <div className="my-3 flex items-center justify-center">
                  <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                    {group.dateLabel}
                  </span>
                </div>

                {group.messages.map((msg) => {
                  if (msg.isSystemMessage) {
                    return (
                      <div key={msg.id} className="my-2 flex justify-center">
                        <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                          {renderSystemMessage(msg.content)}
                        </span>
                      </div>
                    );
                  }

                  const isAdmin = msg.sender?.type === 'admin';
                  const hasReactions = !msg.isDeleted && msg.reactions.length > 0;
                  const hasAttachments = msg.attachments && msg.attachments.length > 0;
                  const canEdit = isAdmin && !msg.isDeleted;

                  return (
                    <div
                      key={msg.id}
                      className={cn('group mb-2 flex items-end gap-1', isAdmin ? 'justify-end' : 'justify-start')}
                    >
                      {/* Action buttons (left for admin msgs) */}
                      {isAdmin && canEdit && (
                        <div className="mb-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="rounded p-1 text-muted-foreground hover:bg-muted" title="Реакция">
                                <Smile className="h-3.5 w-3.5" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-2" side="top" align="end">
                              <div className="flex gap-1">
                                {QUICK_REACTIONS.map((emoji) => {
                                  const existing = msg.reactions.find((r) => r.emoji === emoji);
                                  return (
                                    <button
                                      key={emoji}
                                      className={cn(
                                        'rounded p-1.5 text-base hover:bg-muted',
                                        existing?.hasReacted && 'bg-primary/10',
                                      )}
                                      onClick={() => void toggleReaction(msg.id, emoji, existing?.hasReacted ?? false)}
                                    >
                                      {emoji}
                                    </button>
                                  );
                                })}
                              </div>
                            </PopoverContent>
                          </Popover>
                          <button
                            className="rounded p-1 text-muted-foreground hover:bg-muted"
                            title="Редактировать"
                            onClick={() => startEditing(msg)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}

                      <div className="max-w-[75%]">
                        <div
                          className={cn(
                            'rounded-2xl px-3.5 py-2',
                            isAdmin
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-foreground',
                          )}
                        >
                          {/* Sender name */}
                          {isAdmin && msg.sender && (
                            <p className="mb-0.5 text-[11px] font-medium text-primary-foreground/70">
                              {getSenderName(msg.sender)}
                            </p>
                          )}
                          {!isAdmin && msg.sender && (
                            <p className="mb-0.5 text-[11px] font-medium text-muted-foreground">
                              {getSenderName(msg.sender)}
                            </p>
                          )}

                          {/* Attachments */}
                          {hasAttachments && (
                            <div className="mb-1 space-y-1">
                              {msg.attachments!.map((att) => (
                                isImageMimeType(att.mimeType) ? (
                                  <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" className="block">
                                    <img
                                      src={att.url}
                                      alt={att.name}
                                      className="max-h-48 rounded-lg object-cover"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    key={att.id}
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                      'flex items-center gap-2 rounded-lg p-2',
                                      isAdmin ? 'bg-primary-foreground/10' : 'bg-muted',
                                    )}
                                  >
                                    <FileIcon className="h-4 w-4 shrink-0" />
                                    <div className="min-w-0">
                                      <p className="truncate text-xs font-medium">{att.name}</p>
                                      <p className="text-[10px] opacity-60">{formatFileSize(att.size)}</p>
                                    </div>
                                  </a>
                                )
                              ))}
                            </div>
                          )}

                          {/* Content */}
                          {msg.isDeleted ? (
                            <p className="text-sm italic opacity-50">Сообщение удалено</p>
                          ) : (
                            msg.content && (
                              <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                            )
                          )}

                          {/* Time + status */}
                          <div className={cn(
                            'mt-1 flex items-center gap-1',
                            isAdmin ? 'justify-end' : 'justify-start',
                          )}>
                            <span className={cn(
                              'text-[11px]',
                              isAdmin ? 'text-primary-foreground/50' : 'text-muted-foreground',
                            )}>
                              {formatTime(msg.createdAt)}
                              {msg.isEdited && ' (ред.)'}
                            </span>
                            {getStatusIcon(msg)}
                          </div>
                        </div>

                        {/* Reactions */}
                        {hasReactions && (
                          <div className={cn('mt-1 flex flex-wrap gap-1', isAdmin ? 'justify-end' : 'justify-start')}>
                            {msg.reactions.map((reaction) => (
                              <button
                                key={reaction.emoji}
                                className={cn(
                                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors hover:bg-muted',
                                  reaction.hasReacted
                                    ? 'border-primary/30 bg-primary/10'
                                    : 'border-muted-foreground/20 bg-muted/50',
                                )}
                                onClick={() => void toggleReaction(msg.id, reaction.emoji, reaction.hasReacted)}
                              >
                                <span>{reaction.emoji}</span>
                                {reaction.count > 1 && (
                                  <span className="text-muted-foreground">{reaction.count}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action buttons (right for user msgs) */}
                      {!isAdmin && !msg.isDeleted && (
                        <div className="mb-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="rounded p-1 text-muted-foreground hover:bg-muted" title="Реакция">
                                <Smile className="h-3.5 w-3.5" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-2" side="top" align="start">
                              <div className="flex gap-1">
                                {QUICK_REACTIONS.map((emoji) => {
                                  const existing = msg.reactions.find((r) => r.emoji === emoji);
                                  return (
                                    <button
                                      key={emoji}
                                      className={cn(
                                        'rounded p-1.5 text-base hover:bg-muted',
                                        existing?.hasReacted && 'bg-primary/10',
                                      )}
                                      onClick={() => void toggleReaction(msg.id, emoji, existing?.hasReacted ?? false)}
                                    >
                                      {emoji}
                                    </button>
                                  );
                                })}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Typing indicator */}
      {typingText && (
        <div className="px-4 py-1">
          <span className="text-xs italic text-muted-foreground">{typingText}</span>
        </div>
      )}

      {/* Editing banner */}
      {editingMessage && (
        <div className="flex items-center gap-2 border-t bg-muted/30 px-4 py-2">
          <Pencil className="h-3.5 w-3.5 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-primary">Редактирование</p>
            <p className="truncate text-xs text-muted-foreground">{editingMessage.content}</p>
          </div>
          <button
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            onClick={cancelEditing}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => void handleFileSelect(e)}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt"
      />

      {/* Input */}
      <div className={cn('border-t px-4 py-3', !editingMessage && 'border-t')}>
        <div className="flex items-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 h-9 w-9"
            onClick={() => fileInputRef.current?.click()}
            disabled={roomId === null || isUploading}
            title="Прикрепить файл"
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Paperclip className="h-4 w-4" />
            )}
          </Button>
          <Textarea
            ref={textareaRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={editingMessage ? 'Редактировать сообщение...' : 'Написать сообщение...'}
            className="min-h-[40px] max-h-[120px] resize-none text-sm"
            rows={1}
            disabled={roomId === null}
          />
          <Button
            size="icon"
            onClick={() => void handleSend()}
            disabled={!inputText.trim() || isSending || roomId === null}
            className="shrink-0"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
